import * as React from 'react';

import { DataSourceTable } from 'src/constants/dataSourceTable';
import { VgTableMetadata } from 'src/containers/ProjectNewResourceDialogV2/hooks/useVirtualGraphDialogState';

type UseMetadataFetchQueueParams = {
  checkedVgTables: DataSourceTable[];
  vgTableMetadata: Record<string, VgTableMetadata>;
  localDataSource: { id: string } | null;
  isQuerying: boolean;
  isUsingCustomQuery: boolean;
  nativeQuoteCharacter: string | null;
  tables: DataSourceTable[];
  initialPreviewTable?: string;
  previewTable: string | null;
  setPreviewTable: React.Dispatch<React.SetStateAction<string | null>>;
  setForceRefresh: React.Dispatch<React.SetStateAction<boolean>>;
  enabled?: boolean;
  isManualPreviewRef?: React.MutableRefObject<boolean>;
};

type UseMetadataFetchQueueReturn = {
  metadataFetchQueue: string[];
  setMetadataFetchQueue: React.Dispatch<React.SetStateAction<string[]>>;
  tablesNeedingMetadataRef: React.MutableRefObject<Set<string>>;
  advanceQueue: () => void;
};

/**
 * Hook to manage the metadata fetch queue for virtual graph tables.
 * Handles queueing tables for metadata fetching and processing the queue
 * by temporarily previewing each table to trigger a query.
 */
export const useMetadataFetchQueue = ({
  checkedVgTables,
  vgTableMetadata,
  localDataSource,
  isQuerying,
  isUsingCustomQuery,
  nativeQuoteCharacter,
  tables,
  initialPreviewTable,
  previewTable,
  setPreviewTable,
  setForceRefresh,
  enabled = true,
  isManualPreviewRef,
}: UseMetadataFetchQueueParams): UseMetadataFetchQueueReturn => {
  const tablesNeedingMetadataRef = React.useRef<Set<string>>(new Set());
  const [metadataFetchQueue, setMetadataFetchQueue] = React.useState<string[]>(
    []
  );

  // Queue tables for metadata fetching when checked
  React.useEffect(() => {
    if (!localDataSource || !enabled) {
      return;
    }

    const tablesNeedingMetadata = checkedVgTables
      .filter(
        (table) =>
          !vgTableMetadata[table.name] &&
          !tablesNeedingMetadataRef.current.has(table.name)
      )
      .map((table) => table.name);

    if (tablesNeedingMetadata.length > 0) {
      tablesNeedingMetadata.forEach((name) =>
        tablesNeedingMetadataRef.current.add(name)
      );
      setMetadataFetchQueue((prev) => {
        const newQueue = [
          ...prev,
          ...tablesNeedingMetadata.filter((name) => !prev.includes(name)),
        ];
        return newQueue;
      });
    }
  }, [checkedVgTables, vgTableMetadata, localDataSource, enabled]);

  // Process metadata fetch queue by temporarily previewing each table
  React.useEffect(() => {
    if (
      !enabled ||
      metadataFetchQueue.length === 0 ||
      !localDataSource ||
      isQuerying ||
      isUsingCustomQuery ||
      nativeQuoteCharacter === null
    ) {
      return;
    }

    // If user is viewing a specific table via initialPreviewTable, don't interfere
    if (initialPreviewTable && previewTable === initialPreviewTable) {
      return;
    }

    // Only block queue processing if user has made a manual selection
    const isManualSelection = isManualPreviewRef?.current === true;
    const isOurQueueTable = previewTable === metadataFetchQueue[0];
    if (isManualSelection && previewTable && !isOurQueueTable) {
      // User manually selected a table - don't interrupt their viewing
      return;
    }

    // Verify the table exists in the tables array before trying to fetch metadata
    const tableToFetch = metadataFetchQueue[0];
    const tableExists = tables.some((t) => t.name === tableToFetch);
    if (!tableExists) {
      // Table not found, remove it from queue and continue
      setMetadataFetchQueue((prev) =>
        prev.filter((name) => name !== tableToFetch)
      );
      tablesNeedingMetadataRef.current.delete(tableToFetch);
      return;
    }

    // Set the first table in queue as preview to trigger query
    if (tableToFetch !== previewTable) {
      if (isManualPreviewRef) {
        // eslint-disable-next-line no-param-reassign
        isManualPreviewRef.current = false;
      }
      setPreviewTable(tableToFetch);
      setForceRefresh(true);
    }
  }, [
    enabled,
    metadataFetchQueue,
    localDataSource,
    isQuerying,
    previewTable,
    isUsingCustomQuery,
    nativeQuoteCharacter,
    tables,
    initialPreviewTable,
    setPreviewTable,
    setForceRefresh,
    isManualPreviewRef,
  ]);

  // Helper to advance the queue after processing a table
  const advanceQueue = React.useCallback(() => {
    setMetadataFetchQueue((prev) => {
      if (prev.length === 0) {
        return prev;
      }
      return prev.slice(1);
    });
  }, []);

  return {
    metadataFetchQueue,
    setMetadataFetchQueue,
    tablesNeedingMetadataRef,
    advanceQueue,
  };
};
